import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Pipe,
  PipeTransform,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Note } from '@app/models/note.model';
import { Profile } from '@app/models/profile.model';
import { NameLocalStorage } from '@app/models/record.model';
import { ScheduleTitle } from '@app/models/schedule.model';
import { User } from '@app/models/user.model';
import { AccesslogService } from '@app/services/accesslog.service';
import { ApiService } from '@app/services/api.service';
import { DateService } from '@app/services/date.service';
import { DialogService } from '@app/services/dialog.service';
import { LibService } from '@app/services/lib.service';
import { MessageService } from '@app/services/message.service';
import { RecordService } from '@app/services/record.service';
import { ModalMenuData } from '@app/shared/components/modal-menu/modal-menu.component';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { DataSelectors } from '@app/store/data/data.selectors';
import { SelectUser } from '@app/store/data/data.classes';
import { SettingSelectors } from '@app/store/setting/setting.selectors';
import { SelectTitles } from '@app/store/setting/setting.classes';
import { ScheduleSelectors } from '@app/store/schedule/schedule.selectors';
import { RemoveVisibleOnlyData, TriggerHistory } from '@app/store/schedule/schedule.classes';
import { NoteTitleInputParams } from '@care-connect-japan/ckweblib/lib/data/schema/params/library-params';
import { Select, Store } from '@ngxs/store';
import * as moment from 'moment';
import { AuthService } from '@app/services/auth.service';
import {
  Observable,
  Subject,
  combineLatest,
  filter,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';
import { NoteBlobService } from '@app/services/noteblob.service';

@Component({
  selector: 'app-facesheet',
  templateUrl: './facesheet.component.html',
  styleUrls: ['./facesheet.component.scss'],
})
export class FacesheetComponent implements OnInit, OnDestroy {
  @Select(DataSelectors.selectedUser) user$!: Observable<User>;
  formHistoryList!: Note[];
  form: FormControl;
  currentNote?: Note;
  noteTitleParams!: NoteTitleInputParams;
  currentTitle!: ScheduleTitle;
  allowEdit: boolean = false;
  faceSheetCacheName: string = 'faceSheetCacheAtPage';
  userLocalStorage: any = null;
  conditionFilterStorage: any = {};
  profile!: Profile | null;
  visible: boolean = true;
  @Select(ScheduleSelectors.fetchHistory) reload$!: Observable<Date>;
  @Select(ScheduleSelectors.setVisibleSchedule) visible$!: Observable<String>;
  @ViewChild('titleformDiv') titleformContainer: ElementRef | null = null;
  private onDestory$: Subject<void>;


  get objcd() {
    return this.store.selectSnapshot(AuthSelectors.objectcd);
  }

  get userList(): User[] {
    return this.store.selectSnapshot(DataSelectors.userList(this.objcd));
  }

  constructor(
    private authService: AuthService,
    private dateService: DateService,
    private route: ActivatedRoute,
    private accesslog: AccesslogService,
    private router: Router,
    private message: MessageService,
    private store: Store,
    private apiService: ApiService,
    private dialog: DialogService,
    private libService: LibService,
    private recordService: RecordService,
    private cd: ChangeDetectorRef,
    private noteblobService: NoteBlobService
  ) {
    this.store.dispatch(new RemoveVisibleOnlyData());
    let webview = this.store.selectSnapshot(SettingSelectors.isWebView);
    if (webview) {
      let screenName = 'screen-data';
      localStorage.setItem(screenName + '-isBack', '1');
    }
    this.onDestory$ = new Subject();
    this.form = new FormControl();
    this.user$.pipe(takeUntil(this.onDestory$)).subscribe();
    this.router.events.pipe(
      takeUntil(this.onDestory$),
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    )
      .subscribe((e) => {
        this.allowEdit = this.authService.compRoleCheck(e.url?.split('?')[0]);
      });
  }

  ngOnInit(): void {
    this.visible$.subscribe((res) => {
      this.visible = !(res && res != undefined);
      this.cd.detectChanges();
    });
    this.route.queryParamMap.subscribe((rs: any) => {
      this.getLocalStorage();
      if (rs?.params?.personal !== "true") {
        const findUser = this.userList?.filter((f: any) => f?.daicd === this.userLocalStorage?.daicd);
        if ((this.userLocalStorage !== undefined && this.userLocalStorage !== null && Object.keys(this.userLocalStorage).length > 0) && findUser.length > 0) {
          this.store.dispatch(new SelectUser(this.userLocalStorage));
        }
      }
    })
    this.allowEdit = this.authService.compRoleCheck(this.router.url?.split('?')[0]);
    this.fetchHistory();
  }

  setCache(note: Note | undefined) {
    if (!note) return;
    let { noteid, objcd } = note;
    let cache = {
      url: this.router.url,
      noteid,
      objcd,
    };
    let stringCache = JSON.stringify(cache);
    localStorage.setItem(this.faceSheetCacheName, stringCache);
  }

  getCache() {
    let cache = localStorage.getItem(this.faceSheetCacheName) || '';
    if (!cache) return this.formHistoryList[0];
    let itemParse = JSON.parse(cache);
    if (itemParse.url !== this.router.url) {
      localStorage.removeItem(this.faceSheetCacheName);
      return this.formHistoryList[0];
    }

    return (
      this.formHistoryList.find(
        (x) => x.noteid === itemParse.noteid && x.objcd === itemParse.objcd
      ) || this.formHistoryList[0]
    );
  }

  fetchHistory() {
    const param$ = this.route.paramMap.pipe(
      takeUntil(this.onDestory$),
      map((paramMap) => {
        const objcd = paramMap.get('objcd') || '';
        const ttlkbn = paramMap.get('ttlkbn') || '';
        const ttlcd = paramMap.get('ttlcd') || '';
        this.currentTitle = { ttlkbn, ttlcd, objcd, name: '' };
        return { objcd, ttlkbn, ttlcd };
      })
    );

    combineLatest([
      param$,
      this.user$.pipe(takeUntil(this.onDestory$)),
      this.reload$,
    ])
      .pipe(
        takeUntil(this.onDestory$),
        map(([param, user]) => ({ daicd: user?.daicd, ...param })),
        mergeMap((payload) => this.apiService.getFormHistoryList(payload)),
        switchMap((notes) => {
          if (notes.length > 0) {
            const updatedNotes$ = this.noteblobService.fetchAndMapNoteBlobs(notes);
            // Wait for all note updates to complete
            return forkJoin(updatedNotes$);
          }
          return of(notes);

        })
      )
      .subscribe(async (formHistoryList) => {
        // CKH-1250
        // sort by: date --> noteid
        // reset when formHistoryList refresh
        this.currentNote = undefined;
        this.formHistoryList = formHistoryList.sort((a, b) => {
          let dateA = moment(a.sdate);
          let dateB = moment(b.sdate);
          if (dateA.diff(dateB) > 0) {
            return -1;
          }
          if (dateA.diff(dateB) < 0) {
            return 1;
          }
          return b.noteid - a.noteid;
        });
        if (formHistoryList.length > 0) {
          const noteData = this.getCache();
          this.currentNote = noteData;
          // selection data base on noteid
          // CKH-1247
          this.form.setValue(noteData.noteid);
          this.noteTitleParams = await this.libService.getNoteTitleParams(
            noteData,
            {
              view: true,
            }
          );
        }
      });
  }

  titleHeight() {
    let height = 0;
    if (!this.titleformContainer) {
      return { height: '100%' };
    } else {
      let findHeader =
        this.titleformContainer.nativeElement.querySelector('.form-header');
      let findChild = this.titleformContainer.nativeElement.querySelector(
        '.form-body-container'
      );
      if (findChild) {
        height =
          (findChild?.getBoundingClientRect().height || 0) +
          (findHeader?.getBoundingClientRect().height || 0);
      } else {
        return { height: '100%' };
      }
    }
    return { height: height - 50 + 'px' };
  }

  ngOnDestroy(): void {
    this.onDestory$.next();
    localStorage.removeItem(this.faceSheetCacheName);
    this.store.dispatch(new RemoveVisibleOnlyData());
  }

  onUserChange(user: User) {
    this.getLocalStorage();
    this.conditionFilterStorage[NameLocalStorage.USER] = user;
    this.conditionFilterStorage[NameLocalStorage.CURRENT_STAFF_CODE] =
      this.profile?.staffCode;
    localStorage.setItem(
      NameLocalStorage.CONDITION_FILTER,
      JSON.stringify(this.conditionFilterStorage)
    );
    this.store.dispatch(new SelectUser(user));
    localStorage.removeItem(this.faceSheetCacheName);
  }

  async onDateChange() {
    const noteData = this.formHistoryList.find(
      // CKH-1247
      // 10-10-2023
      // find by id not date
      (item) => item.noteid === this.form.value
    );
    if (noteData) {
      this.currentNote = noteData;
      this.setCache(noteData);
      this.noteTitleParams = await this.libService.getNoteTitleParams(
        noteData!,
        {
          view: true,
        }
      );
    }
  }

  extractOffice(officeStr: string) {
    const corpcd = officeStr?.slice(0, 1) || '';
    const unitcd = officeStr?.slice(officeStr.length - 3) || '';
    const remain =
      officeStr?.slice(1, officeStr.length - 3).match(/.{1,2}/g) || '';
    const officeObject = {
      corpcd,
      officecd: remain[0] || '',
      blockcd: remain[1 || ''],
      subcd: remain[2] || '',
      unitcd,
    };
    return officeObject;
  }

  dialogAction() {
    if (!this.allowEdit) return;
    let disabled = !this.currentNote?.noteid;
    let menuAction: ModalMenuData[] = [
      { label: '編集', value: 'edit', disabled },
      { label: '新規', value: 'new' },
      { label: '複写', value: 'duplicate', disabled },
      { label: '削除', value: 'delete', disabled },
    ];
    const modalDialog = this.dialog.menu(menuAction);
    modalDialog.afterClosed().subscribe((result: string) => {
      if (!result) return;
      if (result === 'delete' && this.currentNote?.noteid) {
        this.delete();
        return;
      }
      if (!this.currentNote?.noteid) {
        this.new();
        return;
      }
      switch (result) {
        case 'edit': {
          this.edit();
          break;
        }
        case 'duplicate': {
          this.duplicate();
          break;
        }
        case 'new': {
          this.new();
          break;
        }
      }
    });
  }

  edit() {
    if (!this.currentNote) return;
    const { noteid, objkbn, objcd } = this.currentNote;
    const path = 'facesheet-edit';
    const routerlink = [
      '',
      {
        outlets: {
          edit: [path, { noteid, objkbn, objcd }],
        },
      },
    ];
    this.goToPage(routerlink);

  }

  duplicate() {
    if (!this.currentNote) return;
    const { noteid, objkbn, objcd } = this.currentNote;
    const path = 'facesheet-duplicate';
    const routerlink = [
      '',
      {
        outlets: {
          edit: [path, noteid, { objkbn, objcd }],
        },
      },
    ];
    this.goToPage(routerlink);
  }

  new() {
    const path = 'facesheet-create';
    const dai = this.store.selectSnapshot(DataSelectors.selectedUser);
    if (!dai || !this.currentTitle) return;
    this.store
      .dispatch(
        new SelectTitles({
          objcd: dai.daicd,
          items: [
            {
              ...this.currentTitle,
              ...this.extractOffice(this.currentTitle.objcd),
              ttlnm: this.currentTitle.name,
              objnm: ''
            },
          ],
        })
      )
      .subscribe(() =>
        this.goToPage(['', { outlets: { edit: [path] } }])
      )
  }

  delete() {
    if (!this.currentNote) return;
    let record = { ...this.currentNote };
    const dai = this.store.selectSnapshot(DataSelectors.selectedUser);
    this.recordService
      .facesheetDelete(record, dai?.name || '')
      .subscribe(() => {
        const operation = this.recordService.getOperation(record.noteid);
        const sdate = this.dateService.formatISO8601(record.sdate);
        const edate = this.dateService.formatISO8601(record.edate);
        const note = {
          ...record,
          sdate,
          edate,
          delstat: 1,
        };
        const displayName = this.accesslog.getKidsDisplayName(record.ttlnm);
        let removeByNoteBlobs = [];
        if (note.delstat === 1 && (note as any).noteblobs.length > 0) {
          removeByNoteBlobs = (note as any).noteblobs.map((noteblob: any) => noteblob.key);
        }
        delete (note as any).noteblobs;

        this.apiService
          .setNoteList([{ operation, note, displayName, removeByNoteBlobs }])
          .subscribe((res) => {
            const errorList = res
              .filter((item) => !!item.error)
              .map((item) => this.message.getErrorMessage(item.error!));

            if (errorList.length > 0) {
              let errorMsg = '削除に失敗しました。\n再度実行してください。';
              this.dialog.message(errorMsg);
            }

            if (errorList.length !== res.length) {
              this.deleteMessage();
            }
          });
      });
  }

  deleteMessage() {
    // case create / no noteid
    this.store.dispatch(new TriggerHistory());
    let text = '記録を削除しました';
    this.dialog.message(text);

    // this.close();
  }

  goToPage(linkOptions: any[]) {
    this.router.navigate(linkOptions, { queryParamsHandling: "preserve" });
  }

  private getLocalStorage(): void {
    this.profile = this.store.selectSnapshot(AuthSelectors.profile);
    const conditionFilterJson: any = localStorage.getItem(
      NameLocalStorage.CONDITION_FILTER
    );
    if (
      conditionFilterJson !== null &&
      Object.keys(conditionFilterJson).length > 0
    ) {
      this.conditionFilterStorage = JSON.parse(conditionFilterJson) || [];
      const staffCode =
        this.conditionFilterStorage[NameLocalStorage.CURRENT_STAFF_CODE] ??
        null;
      if (staffCode === null || this.profile?.staffCode === staffCode) {
        this.userLocalStorage =
          this.conditionFilterStorage[NameLocalStorage.USER];
      }
      // else {
      //   localStorage.removeItem(NameLocalStorage.CONDITION_FILTER);
      // }
    }
  }
}

// pipe for truncate (for NOTE.SUBJECT)
@Pipe({ name: 'truncate' })
export class TruncatePipe implements PipeTransform {
  transform(value: string | undefined, length: number, symbol: string) {
    if (!value || value.length == 0) return '';
    if (value.length > length) {
      return value.slice(0, length) + symbol;
    }
    return value;
  }
}
